/**
 * DemoChatInterface Component
 *
 * A modern, visually appealing chat interface for public demo pages.
 * Supports real-time messaging, typing indicators, smooth animations,
 * and responsive/themed layouts.
 *
 * Validates: Requirements 17.1, 17.2, 17.3, 17.4, 17.5, 17.6, 17.7
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, Fragment } from 'react';
import { resolveWidgetMediaUrl } from '../lib/resolveWidgetMediaUrl';
import {
  fetchDemoThreadFromServer,
  mapServerPayloadToChatMessages,
  reconcileDemoMessages,
  type DemoChatMessage,
} from './demoThreadSync';
import { resolveDemoVisitorId } from './demoVisitorSession';
import { formatMessage } from './formatMessage';
import { AnimatedLogoMark } from '../components/AnimatedLogoMark';
import { AudioRecorder } from './AudioRecorder';
import { DemoAudioPlayer } from './DemoAudioPlayer';
import { DemoImageAlbum } from './DemoImageAlbum';
import {
  groupConsecutiveImageMessages,
  imagesInMessage,
} from './groupConsecutiveImageMessages';
import { LimitDisplay } from './LimitDisplay';
import { LimitReachedOverlay } from './LimitReachedOverlay';

// ============================================
// Types
// ============================================

export interface DemoConfig {
  agentId?: string;
  agentName: string;
  /** Título en la consola (lista de conversaciones) para hilos de esta demo. */
  conversationListTitle?: string;
  logoUrl?: string;
  primaryColor: string;
  accentColor?: string;
  welcomeMessage: string;
  showPoweredBy: boolean;
  theme: 'light' | 'dark' | 'system';
  /** Si true, muestra el micrófono para enviar notas de voz. */
  audioInputEnabled?: boolean;
  /** Si true, el agente puede responder con audio (TTS). */
  audioOutputEnabled?: boolean;
}

type ChatMessage = DemoChatMessage;

interface DemoChatInterfaceProps {
  config: DemoConfig;
  token: string;
  maxConversationLength: number;
  /** Whether this session originates from the landing page (applies session limit) */
  isLandingSession?: boolean;
  /** Si cambia, el chat arranca una conversación nueva (link de test desde la PWA). */
  freshSessionKey?: string | null;
  /** Test abierto desde la PWA: no muestra ni aplica el cupo del link público. */
  operatorTest?: boolean;
  operatorTestPass?: string | null;
  /** Initial limit data from GET /demo/:token response */
  initialLimitInfo?: {
    messagesUsed: number;
    messageLimit: number;
    messagesRemaining: number;
    daysRemaining: number;
    extensionAvailable: boolean;
  };
}

// ============================================
// Helpers
// ============================================

function generateId(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function postDemoVisitorTyping(token: string, visitorId: string, active: boolean): void {
  void fetch(`/demo/${encodeURIComponent(token)}/visitor-typing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ visitorId, active }),
  }).catch(() => {});
}

function resolveTheme(configTheme: 'light' | 'dark' | 'system'): 'light' | 'dark' {
  if (configTheme === 'system') {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  }
  return configTheme;
}

const DEMO_MAX_FILES = 4;
const DEMO_MAX_BYTES_PER_FILE = 3 * 1024 * 1024;
const DEMO_ALLOWED_MIME = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
]);

function readFileAsDemoPayload(file: File): Promise<{ mimeType: string; base64: string; fileName: string; dataUrl: string }> {
  return new Promise((resolve, reject) => {
    if (!DEMO_ALLOWED_MIME.has(file.type)) {
      reject(new Error(`Tipo no permitido: ${file.type || 'desconocido'}`));
      return;
    }
    if (file.size > DEMO_MAX_BYTES_PER_FILE) {
      reject(new Error('Cada archivo debe ser de hasta 3 MB'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const m = /^data:([^;]+);base64,(.+)$/i.exec(dataUrl);
      if (!m) {
        reject(new Error('No se pudo leer el archivo'));
        return;
      }
      resolve({
        mimeType: m[1].toLowerCase(),
        base64: m[2],
        fileName: file.name,
        dataUrl,
      });
    };
    reader.onerror = () => reject(reader.error ?? new Error('Lectura fallida'));
    reader.readAsDataURL(file);
  });
}

// ============================================
// Styles
// ============================================

const keyframesStyle = `
@keyframes demoChatFadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes demoChatTypingDot {
  0%, 60%, 100% {
    opacity: 0.3;
    transform: translateY(0);
  }
  30% {
    opacity: 1;
    transform: translateY(-3px);
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes demoChatPulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

@media (max-width: 520px) {
  .demo-chat-container {
    padding: 0 !important;
    align-items: stretch !important;
    background: var(--demo-card-bg, #ffffff) !important;
  }
  .demo-chat-card {
    max-width: 100% !important;
    width: 100% !important;
    height: 100dvh !important;
    max-height: 100dvh !important;
    border-radius: 0 !important;
    border: none !important;
    box-shadow: none !important;
  }
  .demo-chat-header {
    padding: 12px 16px !important;
  }
  .demo-chat-messages {
    padding: 12px 14px !important;
  }
  .demo-chat-input-area {
    padding: 10px 12px !important;
    padding-bottom: max(10px, env(safe-area-inset-bottom, 10px)) !important;
  }
  .demo-chat-bubble {
    max-width: 85% !important;
  }
}

@media (min-width: 521px) {
  .demo-chat-card {
    max-width: 520px !important;
    height: 88dvh !important;
    max-height: 780px !important;
  }
  .demo-send-btn:hover:not(:disabled) {
    transform: scale(1.05);
    filter: brightness(1.1);
  }
  .demo-attach-btn:hover:not(:disabled) {
    background: var(--demo-input-hover-bg, #f1f5f9) !important;
  }
}
`;

function getThemeColors(theme: 'light' | 'dark', primaryColor: string) {
  if (theme === 'dark') {
    return {
      bg: '#0f172a',
      cardBg: '#1a2236',
      headerBg: 'rgba(26, 34, 54, 0.85)',
      inputBg: '#1e293b',
      inputBorder: '#334155',
      inputHoverBg: '#283548',
      textPrimary: '#f8fafc',
      textSecondary: '#94a3b8',
      textTertiary: '#64748b',
      messageBgVisitor: primaryColor,
      messageBgAgent: '#242f44',
      messageTextVisitor: '#ffffff',
      messageTextAgent: '#e2e8f0',
      borderColor: '#293548',
      shadowColor: 'rgba(0, 0, 0, 0.4)',
      emptyStateBg: 'rgba(30, 41, 59, 0.4)',
    };
  }
  return {
    bg: '#f0f2f5',
    cardBg: '#ffffff',
    headerBg: 'rgba(255, 255, 255, 0.88)',
    inputBg: '#f8fafc',
    inputBorder: '#e2e8f0',
    inputHoverBg: '#f1f5f9',
    textPrimary: '#0f172a',
    textSecondary: '#64748b',
    textTertiary: '#94a3b8',
    messageBgVisitor: primaryColor,
    messageBgAgent: '#f1f5f9',
    messageTextVisitor: '#ffffff',
    messageTextAgent: '#1e293b',
    borderColor: '#e8ecf1',
    shadowColor: 'rgba(0, 0, 0, 0.06)',
    emptyStateBg: 'rgba(241, 245, 249, 0.6)',
  };
}

// ============================================
// Component
// ============================================

export function DemoChatInterface({
  config,
  token,
  maxConversationLength,
  isLandingSession,
  freshSessionKey,
  operatorTest = false,
  operatorTestPass,
  initialLimitInfo,
}: DemoChatInterfaceProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [remoteOperatorTyping, setRemoteOperatorTyping] = useState(false);

  // --- Message limit state ---
  const [limitInfo, setLimitInfo] = useState<{
    messagesUsed: number;
    limit: number;
    messagesRemaining: number;
    daysRemaining?: number;
    extensionAvailable?: boolean;
    isUnavailable?: boolean;
  } | null>(() => {
    if (isLandingSession) {
      // Landing sessions use hardcoded session limit of 15, starts fresh
      return { messagesUsed: 0, limit: 15, messagesRemaining: 15 };
    }
    if (initialLimitInfo && initialLimitInfo.messageLimit !== undefined) {
      return {
        messagesUsed: initialLimitInfo.messagesUsed ?? 0,
        limit: initialLimitInfo.messageLimit,
        messagesRemaining: initialLimitInfo.messagesRemaining ?? initialLimitInfo.messageLimit,
        daysRemaining: initialLimitInfo.daysRemaining,
        extensionAvailable: initialLimitInfo.extensionAvailable ?? true,
      };
    }
    return null;
  });
  const [limitReached, setLimitReached] = useState<{
    type: 'session' | 'daily';
    extensionAvailable: boolean;
  } | null>(null);
  const [extending, setExtending] = useState(false);
  const [extendError, setExtendError] = useState<string | undefined>();
  // Persist visitorId in sessionStorage so it survives page reloads.
  // `fresh` (link desde la PWA) abre una conversación nueva en cada toque.
  const [visitorId] = useState(() =>
    resolveDemoVisitorId(sessionStorage, token, freshSessionKey, generateId),
  );
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() =>
    resolveTheme(config.theme)
  );

  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const visitorTypingIdleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sendingRef = useRef(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isSendingAudio, setIsSendingAudio] = useState(false);

  // Listen for system theme changes when config.theme === 'system'
  useEffect(() => {
    if (config.theme !== 'system') {
      setResolvedTheme(config.theme);
      return;
    }

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      setResolvedTheme(e.matches ? 'dark' : 'light');
    };
    setResolvedTheme(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [config.theme]);

  // Hidratar desde sessionStorage y alinear con el servidor (móvil: pestaña en background puede perder la respuesta del POST).
  useEffect(() => {
    let cancelled = false;
    const storageKey = `demo_messages_${token}_${visitorId}`;
    let local: ChatMessage[] = [];
    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as ChatMessage[];
        if (parsed.length > 0) {
          local = parsed.map((m) => ({
            ...m,
            timestamp:
              m.timestamp instanceof Date ? m.timestamp : new Date(String((m as { timestamp: unknown }).timestamp)),
          }));
        }
      } catch {
        /* ignore */
      }
    }

    const pullFromServer = async () => {
      try {
        const { messages: payload, operatorTyping } = await fetchDemoThreadFromServer(token, visitorId);
        if (cancelled) return;
        setRemoteOperatorTyping(operatorTyping);
        const serverMsgs = mapServerPayloadToChatMessages(payload);
        setMessages((prev) => {
          const base = prev.length > 0 ? prev : local;
          return reconcileDemoMessages(base, serverMsgs);
        });
      } catch {
        if (!cancelled) setMessages(local);
      }
    };

    void pullFromServer();

    const tick = () => {
      if (cancelled) return;
      void pullFromServer();
    };

    let intervalId = window.setInterval(
      tick,
      document.visibilityState === 'visible' ? 1600 : 5000
    );

    const onVis = () => {
      if (cancelled) return;
      window.clearInterval(intervalId);
      intervalId = window.setInterval(
        tick,
        document.visibilityState === 'visible' ? 1600 : 5000
      );
      if (document.visibilityState === 'visible') void pullFromServer();
    };

    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [token, visitorId, config.welcomeMessage]);

  // Al volver a primer plano: traer mensajes desde BD por si el fetch del envío quedó colgado o el SO mató el JS.
  useEffect(() => {
    const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      void (async () => {
        try {
          let { messages: payload, operatorTyping } = await fetchDemoThreadFromServer(token, visitorId);
          let serverMsgs = mapServerPayloadToChatMessages(payload);
          setRemoteOperatorTyping(operatorTyping);
          setMessages((prev) => reconcileDemoMessages(prev, serverMsgs));
          setIsTyping(false);

          const last = serverMsgs[serverMsgs.length - 1];
          if (last?.role === 'visitor') {
            for (let i = 0; i < 10; i++) {
              await sleep(1600);
              if (document.visibilityState !== 'visible') return;
              const fetched = await fetchDemoThreadFromServer(token, visitorId);
              payload = fetched.messages;
              operatorTyping = fetched.operatorTyping;
              setRemoteOperatorTyping(operatorTyping);
              serverMsgs = mapServerPayloadToChatMessages(payload);
              setMessages((prev) => reconcileDemoMessages(prev, serverMsgs));
              const tail = serverMsgs[serverMsgs.length - 1];
              if (!tail || tail.role === 'agent') break;
            }
          }
        } catch (e) {
          console.warn('[demo-chat] sync visibilidad falló', e);
        }
      })();
    };

    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [token, visitorId]);

  // Save messages to sessionStorage whenever they change (sin adjuntos del visitante: evita cuota excedida)
  useEffect(() => {
    if (messages.length > 0) {
      const storageKey = `demo_messages_${token}_${visitorId}`;
      const stripped = messages.map((m) =>
        m.role === 'visitor' && m.attachments?.length ? { ...m, attachments: undefined } : m
      );
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(stripped));
      } catch (e) {
        console.warn('[demo-chat] no se pudo guardar mensajes en sesión', e);
      }
    }
  }, [messages, token, visitorId]);

  const handleMessagesScroll = useCallback(() => {
    const el = messagesScrollRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= 80;
  }, []);

  // Auto-scroll solo si el usuario está al final (evita saltos al leer historial durante el polling).
  useLayoutEffect(() => {
    const el = messagesScrollRef.current;
    if (!el || !stickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, isTyping, remoteOperatorTyping]);

  const conversationLength = messages.filter((m) => m.role === 'visitor').length;
  const isLimitReached = conversationLength >= maxConversationLength;
  const isNearLimit = conversationLength >= maxConversationLength - 3 && !isLimitReached;

  const sendMessage = useCallback(
    async (text: string, files: File[]) => {
      const trimmed = text.trim();
      if (sendingRef.current || (!trimmed && files.length === 0) || isTyping || isLimitReached || limitReached) return;

      sendingRef.current = true;
      let payloads: Awaited<ReturnType<typeof readFileAsDemoPayload>>[] = [];
      try {
        payloads = await Promise.all(files.map((f) => readFileAsDemoPayload(f)));
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'No se pudieron leer los archivos';
        setMessages((prev) => [
          ...prev,
          {
            id: generateId(),
            role: 'agent' as const,
            text: msg,
            timestamp: new Date(),
          },
        ]);
        sendingRef.current = false;
        return;
      }

      const previewAttachments =
        payloads.length > 0 ?
          payloads.map((p) => ({
            kind: p.mimeType === 'application/pdf' ? 'document' : 'image',
            mimeType: p.mimeType,
            caption: p.fileName,
            url: p.dataUrl,
          }))
        : undefined;

      const userMessage: ChatMessage = {
        id: generateId(),
        role: 'visitor',
        text: trimmed || '[Archivos adjuntos]',
        timestamp: new Date(),
        attachments: previewAttachments,
      };

      stickToBottomRef.current = true;
      setMessages((prev) => [...prev, userMessage]);
      setInputValue('');
      setPendingFiles([]);
      if (visitorTypingIdleRef.current) {
        clearTimeout(visitorTypingIdleRef.current);
        visitorTypingIdleRef.current = null;
      }
      postDemoVisitorTyping(token, visitorId, false);
      setIsTyping(true);

      const requestBody: Record<string, unknown> = {
        message: trimmed,
        visitorId,
        conversationLength: conversationLength + 1,
      };
      if (isLandingSession) {
        requestBody.sessionId = visitorId;
      }
      if (operatorTestPass) {
        requestBody.operatorTestPass = operatorTestPass;
      }
      if (payloads.length > 0) {
        requestBody.attachments = payloads.map((p) => ({
          mimeType: p.mimeType,
          base64: p.base64,
          fileName: p.fileName,
        }));
      }

      const MAX_RETRIES = 5;
      const RETRY_DELAY = 1000;
      let lastError: unknown = null;
      let success = false;

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          const response = await fetch(`/demo/${token}/message`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
          });

          const data = await response.json();

          if (data.success && data.data) {
            const d = data.data as {
              duplicate?: boolean;
              pending?: boolean;
              noAgentReply?: boolean;
              text: string;
              attachments?: ChatMessage['attachments'];
              extraMessages?: Array<{ text: string; attachments?: ChatMessage['attachments'] }>;
              // Limit count fields from API
              messagesUsed?: number;
              messageLimit?: number;
              sessionLimit?: number;
              messagesRemaining?: number;
              daysRemaining?: number;
              extensionAvailable?: boolean;
            };

            // Update limit info from successful response
            if (d.messageLimit !== undefined) {
              setLimitInfo({
                messagesUsed: d.messagesUsed ?? 0,
                limit: d.messageLimit,
                messagesRemaining: d.messagesRemaining ?? 0,
                daysRemaining: d.daysRemaining,
                extensionAvailable: d.extensionAvailable ?? false,
              });
            } else if (d.sessionLimit !== undefined) {
              setLimitInfo({
                messagesUsed: d.messagesUsed ?? 0,
                limit: d.sessionLimit,
                messagesRemaining: d.messagesRemaining ?? 0,
              });
            }

            if (d.duplicate) {
              success = true;
              break;
            }
            if (d.noAgentReply) {
              success = true;
              break;
            }
            const toAppend: ChatMessage[] = [];
            const mainText = (d.text ?? '').trim();
            const mainAttachments =
              Array.isArray(d.attachments) && d.attachments.length > 0 ? d.attachments : undefined;
            if (mainText || mainAttachments) {
              toAppend.push({
                id: generateId(),
                role: 'agent',
                text: d.text,
                timestamp: new Date(),
                attachments: mainAttachments,
              });
            }
            if (Array.isArray(d.extraMessages)) {
              for (const ex of d.extraMessages) {
                toAppend.push({
                  id: generateId(),
                  role: 'agent',
                  text: ex.text,
                  timestamp: new Date(),
                  attachments:
                    Array.isArray(ex.attachments) && ex.attachments.length > 0 ? ex.attachments : undefined,
                });
              }
            }
            setMessages((prev) => [...prev, ...toAppend]);
            success = true;
            break;
          } else if (response.status >= 500) {
            // Internal server error — retry (don't show technical errors to user)
            lastError = new Error(data.error?.message ?? `HTTP ${response.status}`);
            if (attempt < MAX_RETRIES) {
              await new Promise((r) => setTimeout(r, RETRY_DELAY));
            }
          } else {
            // Client error (rate limit, validation, etc.) — show user-friendly message, don't retry
            const errorCode = data.error?.code ?? '';

            // Handle message limit errors specifically
            if (errorCode === 'SESSION_LIMIT_REACHED') {
              setLimitReached({ type: 'session', extensionAvailable: false });
              if (data.data) {
                setLimitInfo({
                  messagesUsed: data.data.messagesUsed ?? 0,
                  limit: data.data.sessionLimit ?? 15,
                  messagesRemaining: 0,
                });
              }
              success = true;
              break;
            } else if (errorCode === 'DAILY_LIMIT_REACHED') {
              setLimitReached({
                type: 'daily',
                extensionAvailable: data.data?.extensionAvailable ?? false,
              });
              if (data.data) {
                setLimitInfo({
                  messagesUsed: data.data.messagesUsed ?? 0,
                  limit: data.data.messageLimit ?? 0,
                  messagesRemaining: 0,
                  daysRemaining: data.data.daysRemaining,
                  extensionAvailable: data.data.extensionAvailable ?? false,
                });
              }
              success = true;
              break;
            } else if (errorCode === 'DEMO_LINK_EXPIRED') {
              setLimitReached({ type: 'daily', extensionAvailable: false });
              if (data.data) {
                setLimitInfo({
                  messagesUsed: data.data.messagesUsed ?? 0,
                  limit: data.data.messageLimit ?? 0,
                  messagesRemaining: 0,
                  daysRemaining: 0,
                  extensionAvailable: false,
                });
              }
              success = true;
              break;
            } else if (errorCode === 'COUNT_UNAVAILABLE') {
              setLimitInfo((prev) => prev ? { ...prev, isUnavailable: true } : { messagesUsed: 0, limit: 0, messagesRemaining: 0, isUnavailable: true });
              // Don't show overlay, just mark as unavailable and show generic error
            }

            const friendlyMessages: Record<string, string> = {
              RATE_LIMITED: 'Estás enviando mensajes muy rápido. Esperá un momento e intentá de nuevo.',
              CONVERSATION_LIMIT: 'Se alcanzó el límite de mensajes para esta conversación.',
              DEMO_UNAVAILABLE: 'Esta demo no está disponible en este momento.',
              SESSION_LIMIT_REACHED: 'Has alcanzado el límite de mensajes para esta sesión.',
              DAILY_LIMIT_REACHED: 'Has alcanzado el límite de mensajes para esta demo.',
              DEMO_LINK_EXPIRED: 'Esta demo ha expirado.',
              COUNT_UNAVAILABLE: 'Servicio temporalmente no disponible. Intentá de nuevo.',
            };
            const friendlyText = friendlyMessages[errorCode]
              ?? data.error?.message
              ?? 'Lo siento, ocurrió un error. Intenta de nuevo.';
            const errorMessage: ChatMessage = {
              id: generateId(),
              role: 'agent',
              text: friendlyText,
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, errorMessage]);
            success = true; // Not a connection error, don't retry
            break;
          }
        } catch (e) {
          lastError = e;
          if (attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, RETRY_DELAY));
          }
        }
      }

      if (!success) {
        console.error('[demo-chat] todos los reintentos fallaron:', lastError);
        const handoffMessage: ChatMessage = {
          id: generateId(),
          role: 'agent',
          text: 'En este momento estamos redirigiendo tu conversación a un agente humano para que pueda atenderte a la brevedad. Disculpá las molestias.',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, handoffMessage]);
      }

      setIsTyping(false);
      sendingRef.current = false;
      // Restore focus to input after sending
      setTimeout(() => inputRef.current?.focus(), 50);
    },
    [token, visitorId, conversationLength, isTyping, isLimitReached, limitReached, isLandingSession, operatorTestPass]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(inputValue, pendingFiles);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue, pendingFiles);
    }
  };

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const picked = input.files ? Array.from(input.files) : [];
    if (picked.length) {
      setPendingFiles((prev) => [...prev, ...picked].slice(0, DEMO_MAX_FILES));
    }
    input.value = '';
  };

  const handleRecordingComplete = useCallback(
    async (blob: Blob, mimeType: string) => {
      setIsRecording(false);
      if (blob.size === 0 || isTyping || isLimitReached || limitReached) return;

      setIsSendingAudio(true);
      stickToBottomRef.current = true;

      // Encode blob to base64
      let audioBase64: string;
      try {
        const arrayBuffer = await blob.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        audioBase64 = btoa(binary);
      } catch {
        setIsSendingAudio(false);
        return;
      }

      // Show optimistic visitor message with audio attachment for inline player
      const audioBlobUrl = URL.createObjectURL(blob);
      const userMessage: ChatMessage = {
        id: generateId(),
        role: 'visitor',
        text: '🎤 Audio',
        timestamp: new Date(),
        attachments: [{ kind: 'audio', mimeType, url: audioBlobUrl }],
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsTyping(true);

      // Send POST with audio payload
      const requestBody: Record<string, unknown> = {
        message: '',
        visitorId,
        conversationLength: conversationLength + 1,
        audioBase64,
        audioMimeType: mimeType,
      };
      if (isLandingSession) {
        requestBody.sessionId = visitorId;
      }
      if (operatorTestPass) {
        requestBody.operatorTestPass = operatorTestPass;
      }

      try {
        const response = await fetch(`/demo/${token}/message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });

        const data = await response.json();

        if (data.success && data.data) {
          const d = data.data as {
            duplicate?: boolean;
            pending?: boolean;
            noAgentReply?: boolean;
            text: string;
            attachments?: ChatMessage['attachments'];
            extraMessages?: Array<{ text: string; attachments?: ChatMessage['attachments'] }>;
          };
          if (!d.duplicate && !d.noAgentReply) {
            const toAppend: ChatMessage[] = [];
            const mainText = (d.text ?? '').trim();
            const mainAttachments =
              Array.isArray(d.attachments) && d.attachments.length > 0 ? d.attachments : undefined;
            if (mainText || mainAttachments) {
              toAppend.push({
                id: generateId(),
                role: 'agent',
                text: d.text,
                timestamp: new Date(),
                attachments: mainAttachments,
              });
            }
            if (Array.isArray(d.extraMessages)) {
              for (const ex of d.extraMessages) {
                toAppend.push({
                  id: generateId(),
                  role: 'agent',
                  text: ex.text,
                  timestamp: new Date(),
                  attachments:
                    Array.isArray(ex.attachments) && ex.attachments.length > 0 ? ex.attachments : undefined,
                });
              }
            }
            setMessages((prev) => [...prev, ...toAppend]);
          }
        } else if (response.status < 500) {
          const friendlyMessages: Record<string, string> = {
            RATE_LIMITED: 'Estás enviando mensajes muy rápido. Esperá un momento e intentá de nuevo.',
            CONVERSATION_LIMIT: 'Se alcanzó el límite de mensajes para esta conversación.',
            DEMO_UNAVAILABLE: 'Esta demo no está disponible en este momento.',
          };
          const errorCode = data.error?.code ?? '';
          const friendlyText = friendlyMessages[errorCode]
            ?? data.error?.message
            ?? 'Lo siento, ocurrió un error. Intenta de nuevo.';
          setMessages((prev) => [
            ...prev,
            { id: generateId(), role: 'agent', text: friendlyText, timestamp: new Date() },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              id: generateId(),
              role: 'agent',
              text: 'En este momento estamos redirigiendo tu conversación a un agente humano para que pueda atenderte a la brevedad. Disculpá las molestias.',
              timestamp: new Date(),
            },
          ]);
        }
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            id: generateId(),
            role: 'agent',
            text: 'En este momento estamos redirigiendo tu conversación a un agente humano para que pueda atenderte a la brevedad. Disculpá las molestias.',
            timestamp: new Date(),
          },
        ]);
      }

      setIsTyping(false);
      setIsSendingAudio(false);
    },
    [token, visitorId, conversationLength, isTyping, isLimitReached, limitReached, isLandingSession, operatorTestPass]
  );

  const canSend =
    (inputValue.trim().length > 0 || pendingFiles.length > 0) && !isTyping && !isLimitReached && !limitReached;

  // --- Message limit handlers ---
  async function handleExtend() {
    setExtending(true);
    setExtendError(undefined);
    try {
      const resp = await fetch(`/demo/${encodeURIComponent(token)}/extend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitorId }),
      });
      const json = await resp.json();
      if (json.success && json.data) {
        setLimitReached(null);
        setLimitInfo({
          messagesUsed: json.data.messagesUsed ?? 0,
          limit: json.data.messageLimit ?? 0,
          messagesRemaining: json.data.messagesRemaining ?? 0,
          daysRemaining: json.data.daysRemaining,
          extensionAvailable: false,
        });
      } else {
        setExtendError(json.error?.message || 'No se pudo extender el acceso.');
      }
    } catch {
      setExtendError('Error de conexión. Intentá de nuevo.');
    } finally {
      setExtending(false);
    }
  }

  function handleScheduleDemo() {
    window.location.href = '/';
  }

  const colors = getThemeColors(resolvedTheme, config.primaryColor || '#7c3aed');

  // ============================================
  // Render
  // ============================================

  return (
    <div
      className="demo-chat-container"
      style={{
        ...containerStyle,
        background: colors.bg,
        // CSS custom props for media query usage
        ['--demo-card-bg' as string]: colors.cardBg,
        ['--demo-input-hover-bg' as string]: colors.inputHoverBg,
      }}
    >
      <style>{keyframesStyle}</style>

      <div
        className="demo-chat-card"
        style={{
          ...chatCardStyle,
          background: colors.cardBg,
          boxShadow: `0 8px 40px ${colors.shadowColor}, 0 2px 8px ${colors.shadowColor}`,
          border: `1px solid ${colors.borderColor}`,
        }}
      >
        {/* Header */}
        <div
          className="demo-chat-header"
          style={{
            ...headerStyle,
            background: colors.headerBg,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderBottom: `1px solid ${colors.borderColor}`,
          }}
        >
          <div style={headerContentStyle}>
            {config.logoUrl ? (
              <img
                src={config.logoUrl}
                alt={`${config.agentName} avatar`}
                style={avatarImgStyle}
              />
            ) : (
              <div
                style={{
                  ...avatarPlaceholderStyle,
                  background: `linear-gradient(135deg, ${config.primaryColor || '#7c3aed'}, ${config.primaryColor || '#7c3aed'}cc)`,
                }}
              >
                {config.agentName.charAt(0).toUpperCase()}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...agentNameStyle, color: colors.textPrimary }}>
                {config.agentName}
              </div>
              <div style={{ ...statusStyle, color: colors.textSecondary }}>
                <span style={statusDotStyle} />
                En línea
              </div>
            </div>
            {limitInfo && !operatorTest ? (
              <LimitDisplay
                messagesUsed={limitInfo.messagesUsed}
                limit={limitInfo.limit}
                messagesRemaining={limitInfo.messagesRemaining}
                daysRemaining={limitInfo.daysRemaining}
                isUnavailable={limitInfo.isUnavailable}
                onScheduleDemo={isLandingSession ? handleScheduleDemo : undefined}
              />
            ) : null}
          </div>
        </div>

        {/* Messages */}
        <div
          ref={messagesScrollRef}
          onScroll={handleMessagesScroll}
          className="demo-chat-messages"
          style={{ ...messagesContainerStyle, background: colors.cardBg, position: 'relative' }}
        >
          {messages.length === 0 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '40px 24px',
                textAlign: 'center',
                gap: '16px',
              }}
            >
              <div style={{
                padding: 20,
                borderRadius: '50%',
                background: colors.emptyStateBg,
              }}>
                <AnimatedLogoMark size={56} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: '15px',
                    fontWeight: 600,
                    color: colors.textPrimary,
                    lineHeight: 1.4,
                  }}
                >
                  ¡Hola! 👋
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: 400,
                    color: colors.textSecondary,
                    lineHeight: 1.5,
                    maxWidth: 260,
                  }}
                >
                  Escribí tu consulta y te respondo al instante
                </p>
              </div>
            </div>
          )}

          {groupConsecutiveImageMessages(messages).map((item) => {
            if (item.type === 'album') {
              return (
                <div
                  key={item.id}
                  style={{
                    ...messageRowStyle,
                    justifyContent: item.role === 'visitor' ? 'flex-end' : 'flex-start',
                    animation: 'demoChatFadeIn 0.25s ease forwards',
                  }}
                >
                  <div style={{ maxWidth: 'min(280px, 85%)' }}>
                    <DemoImageAlbum images={item.images} />
                  </div>
                </div>
              );
            }

            const msg = item.message;
            const albumImages = imagesInMessage(msg);
            const nonImageAttachments = (msg.attachments ?? []).filter((a) => {
              if (a.kind === 'image') return false;
              if ((a.mimeType || '').toLowerCase().startsWith('image/')) return false;
              return true;
            });

            return (
            <div
              key={msg.id}
              style={{
                ...messageRowStyle,
                justifyContent: msg.role === 'visitor' ? 'flex-end' : 'flex-start',
                animation: 'demoChatFadeIn 0.25s ease forwards',
              }}
            >
              <div
                className="demo-chat-bubble"
                style={{
                  ...messageBubbleStyle,
                  background:
                    msg.role === 'visitor'
                      ? colors.messageBgVisitor
                      : colors.messageBgAgent,
                  color:
                    msg.role === 'visitor'
                      ? colors.messageTextVisitor
                      : colors.messageTextAgent,
                  borderBottomRightRadius: msg.role === 'visitor' ? '6px' : '18px',
                  borderBottomLeftRadius: msg.role === 'agent' ? '6px' : '18px',
                  whiteSpace: msg.role === 'visitor' ? 'pre-wrap' : undefined,
                  boxShadow: msg.role === 'visitor'
                    ? '0 1px 3px rgba(0,0,0,0.1)'
                    : `0 1px 2px ${colors.shadowColor}`,
                }}
              >
                {msg.role === 'agent' ? (
                  <Fragment>
                    {msg.text.trim() ? formatMessage(msg.text) : null}
                    {albumImages.length > 0 ? <DemoImageAlbum images={albumImages} /> : null}
                    {nonImageAttachments.length ? (
                      <div
                        style={{
                          marginTop: 10,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                          maxWidth: 'min(280px, 100%)',
                        }}
                      >
                        {nonImageAttachments.map((a, ai) => {
                          const src = resolveWidgetMediaUrl(a.url);
                          if (a.kind === 'audio') {
                            if (!src) {
                              return (
                                <span key={`${msg.id}-att-${ai}`} style={{ fontSize: 12, opacity: 0.75 }}>
                                  🎤 Audio
                                </span>
                              );
                            }
                            return (
                              <DemoAudioPlayer
                                key={`${msg.id}-att-${ai}`}
                                url={src}
                                accentColor={config.primaryColor}
                              />
                            );
                          }
                          if (a.kind === 'video') {
                            return (
                              <video
                                key={`${msg.id}-att-${ai}`}
                                src={src}
                                controls
                                playsInline
                                style={{ width: '100%', borderRadius: 8, maxHeight: 220 }}
                              />
                            );
                          }
                          if (a.kind === 'document' || a.mimeType === 'application/pdf') {
                            return (
                              <a
                                key={`${msg.id}-att-${ai}`}
                                href={src}
                                target="_blank"
                                rel="noreferrer"
                                download={a.caption || 'adjunto.pdf'}
                                style={{
                                  color: 'inherit',
                                  fontWeight: 600,
                                  fontSize: 13,
                                  textDecoration: 'underline',
                                }}
                              >
                                {a.caption?.trim() ? `📄 ${a.caption}` : '📄 Ver PDF'}
                              </a>
                            );
                          }
                          return null;
                        })}
                      </div>
                    ) : null}
                  </Fragment>
                ) : (
                  <Fragment>
                    {(() => {
                      const audioAttachment = msg.attachments?.find(a => a.kind === 'audio');
                      if (audioAttachment) {
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 }}>
                            <DemoAudioPlayer
                              url={resolveWidgetMediaUrl(audioAttachment.url)}
                              accentColor={config.primaryColor}
                            />
                            {msg.audioTranscription && (
                              <span
                                style={{
                                  fontSize: 12,
                                  opacity: 0.75,
                                  fontStyle: 'italic',
                                  lineHeight: 1.4,
                                  marginTop: 2,
                                }}
                              >
                                {msg.audioTranscription}
                              </span>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}
                    {!msg.attachments?.find(a => a.kind === 'audio') && msg.text.trim() ? (
                      <span style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</span>
                    ) : null}
                    {albumImages.length > 0 ? <DemoImageAlbum images={albumImages} /> : null}
                    {nonImageAttachments.filter((a) => a.kind !== 'audio').length ? (
                      <div
                        style={{
                          marginTop: 10,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                          maxWidth: 'min(280px, 100%)',
                        }}
                      >
                        {nonImageAttachments.filter((a) => a.kind !== 'audio').map((a, ai) => {
                          const src = resolveWidgetMediaUrl(a.url);
                          if (a.kind === 'video') {
                            return (
                              <video
                                key={`${msg.id}-att-${ai}`}
                                src={src}
                                controls
                                playsInline
                                style={{ width: '100%', borderRadius: 8, maxHeight: 220 }}
                              />
                            );
                          }
                          if (a.kind === 'document' || a.mimeType === 'application/pdf') {
                            return (
                              <a
                                key={`${msg.id}-att-${ai}`}
                                href={src}
                                target="_blank"
                                rel="noreferrer"
                                download={a.caption || 'adjunto.pdf'}
                                style={{
                                  color: 'inherit',
                                  fontWeight: 600,
                                  fontSize: 13,
                                  textDecoration: 'underline',
                                }}
                              >
                                {a.caption?.trim() ? `📄 ${a.caption}` : '📄 Ver PDF'}
                              </a>
                            );
                          }
                          return null;
                        })}
                      </div>
                    ) : null}
                  </Fragment>
                )}
              </div>
            </div>
            );
          })}

          {/* Typing indicator */}
          {(isTyping || remoteOperatorTyping) && (
            <div
              style={{
                ...messageRowStyle,
                justifyContent: 'flex-start',
                animation: 'demoChatFadeIn 0.2s ease forwards',
              }}
            >
              <div
                className="demo-chat-bubble"
                style={{
                  ...messageBubbleStyle,
                  background: colors.messageBgAgent,
                  padding: '14px 20px',
                  display: 'flex',
                  gap: '5px',
                  alignItems: 'center',
                  borderBottomLeftRadius: '6px',
                  boxShadow: `0 1px 2px ${colors.shadowColor}`,
                }}
              >
                <span
                  style={{
                    ...typingDotStyle,
                    background: colors.textSecondary,
                    animationDelay: '0ms',
                  }}
                />
                <span
                  style={{
                    ...typingDotStyle,
                    background: colors.textSecondary,
                    animationDelay: '150ms',
                  }}
                />
                <span
                  style={{
                    ...typingDotStyle,
                    background: colors.textSecondary,
                    animationDelay: '300ms',
                  }}
                />
              </div>
            </div>
          )}

          {/* Conversation limit warning */}
          {isNearLimit && (
            <div style={{ ...limitWarningStyle, color: colors.textSecondary }}>
              Quedan {maxConversationLength - conversationLength} mensajes en esta conversación
            </div>
          )}

          {isLimitReached && (
            <div style={{ ...limitWarningStyle, color: '#ef4444' }}>
              Se alcanzó el límite de mensajes para esta demo
            </div>
          )}

          {/* Message limit reached overlay */}
          {limitReached && (
            <LimitReachedOverlay
              type={limitReached.type}
              extensionAvailable={limitReached.extensionAvailable}
              onExtend={handleExtend}
              onScheduleDemo={handleScheduleDemo}
              extending={extending}
              extendError={extendError}
            />
          )}

        </div>

        {/* Input */}
        <form
          onSubmit={handleSubmit}
          className="demo-chat-input-area"
          style={{
            ...inputContainerStyle,
            flexDirection: 'column',
            alignItems: 'stretch',
            borderTop: `1px solid ${colors.borderColor}`,
            background: colors.cardBg,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
            style={{ display: 'none' }}
            onChange={onPickFiles}
            aria-hidden
          />
          {pendingFiles.length > 0 ? (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                marginBottom: 4,
                width: '100%',
              }}
            >
              {pendingFiles.map((f, i) => (
                <span
                  key={`${f.name}-${i}-${f.size}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 10px',
                    borderRadius: 999,
                    fontSize: 12,
                    background: colors.inputBg,
                    border: `1px solid ${colors.inputBorder}`,
                    color: colors.textPrimary,
                    maxWidth: '100%',
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: 16,
                      lineHeight: 1,
                      color: colors.textSecondary,
                    }}
                    aria-label={`Quitar ${f.name}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%' }}>
            {!isRecording && (
              <>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isTyping || isLimitReached || !!limitReached || pendingFiles.length >= DEMO_MAX_FILES}
                  title="Adjuntar archivos (imágenes o PDF, máx. 4)"
                  className="demo-attach-btn"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: 'none',
                    background: colors.inputBg,
                    color: colors.textSecondary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor:
                      isTyping || isLimitReached || !!limitReached || pendingFiles.length >= DEMO_MAX_FILES ?
                        'not-allowed'
                      : 'pointer',
                    flexShrink: 0,
                    opacity:
                      isTyping || isLimitReached || !!limitReached || pendingFiles.length >= DEMO_MAX_FILES ? 0.4 : 1,
                    transition: 'background 150ms ease, opacity 150ms ease',
                  }}
                  aria-label="Adjuntar archivos"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                </button>
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => {
                    const v = e.target.value;
                    setInputValue(v);
                    if (visitorTypingIdleRef.current) {
                      clearTimeout(visitorTypingIdleRef.current);
                      visitorTypingIdleRef.current = null;
                    }
                    if (!v.trim()) {
                      postDemoVisitorTyping(token, visitorId, false);
                      return;
                    }
                    postDemoVisitorTyping(token, visitorId, true);
                    visitorTypingIdleRef.current = setTimeout(
                      () => postDemoVisitorTyping(token, visitorId, false),
                      2200
                    );
                  }}
                  onBlur={() => {
                    if (visitorTypingIdleRef.current) {
                      clearTimeout(visitorTypingIdleRef.current);
                      visitorTypingIdleRef.current = null;
                    }
                    postDemoVisitorTyping(token, visitorId, false);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    isLimitReached || limitReached ? 'Límite alcanzado' : 'Escribí un mensaje…'
                  }
                  disabled={isTyping || isLimitReached || !!limitReached}
                  style={{
                    ...inputStyle,
                    background: colors.inputBg,
                    border: `1.5px solid ${colors.inputBorder}`,
                    color: colors.textPrimary,
                  }}
                  aria-label="Mensaje"
                />
              </>
            )}
            {config.audioInputEnabled ? (
              <AudioRecorder
                onRecordingComplete={(blob, mimeType) => {
                  setIsRecording(false);
                  handleRecordingComplete(blob, mimeType);
                }}
                onRecordingStart={() => setIsRecording(true)}
                disabled={isTyping || isLimitReached || !!limitReached || isSendingAudio}
              />
            ) : null}
            {!isRecording && (
              <button
                type="submit"
                disabled={!canSend}
                className="demo-send-btn"
                style={{
                  ...sendButtonStyle,
                  background: canSend
                    ? `linear-gradient(135deg, ${config.primaryColor || '#7c3aed'}, ${config.primaryColor || '#7c3aed'}dd)`
                    : colors.inputBg,
                  color: canSend ? '#ffffff' : colors.textTertiary,
                  opacity: 1,
                  cursor: !canSend ? 'default' : 'pointer',
                  border: canSend ? 'none' : `1.5px solid ${colors.inputBorder}`,
                }}
                aria-label="Enviar mensaje"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            )}
          </div>
        </form>

        {/* Powered by footer */}
        {config.showPoweredBy && (
          <div style={{ ...poweredByStyle, color: colors.textTertiary, borderTop: `1px solid ${colors.borderColor}` }}>
            Powered by <strong style={{ fontWeight: 600 }}>Virtual Agents</strong>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Style constants
// ============================================

const containerStyle: React.CSSProperties = {
  minHeight: '100dvh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
  fontFamily: "'DM Sans', Inter, system-ui, -apple-system, sans-serif",
  transition: 'background 0.3s ease',
};

const chatCardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: '520px',
  height: '88dvh',
  maxHeight: '780px',
  borderRadius: '20px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  transition: 'box-shadow 0.3s ease',
};

const headerStyle: React.CSSProperties = {
  padding: '14px 20px',
  flexShrink: 0,
  position: 'sticky',
  top: 0,
  zIndex: 10,
};

const headerContentStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
};

const avatarImgStyle: React.CSSProperties = {
  width: '38px',
  height: '38px',
  borderRadius: '50%',
  objectFit: 'cover',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
};

const avatarPlaceholderStyle: React.CSSProperties = {
  width: '38px',
  height: '38px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#ffffff',
  fontWeight: 700,
  fontSize: '15px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
};

const agentNameStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: '15px',
  letterSpacing: '-0.01em',
};

const statusStyle: React.CSSProperties = {
  fontSize: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  marginTop: '1px',
};

const statusDotStyle: React.CSSProperties = {
  width: '7px',
  height: '7px',
  borderRadius: '50%',
  background: '#22c55e',
  display: 'inline-block',
  boxShadow: '0 0 0 2px rgba(34, 197, 94, 0.2)',
};

const messagesContainerStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '16px 18px',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  WebkitOverflowScrolling: 'touch',
};

const messageRowStyle: React.CSSProperties = {
  display: 'flex',
  width: '100%',
};

const messageBubbleStyle: React.CSSProperties = {
  maxWidth: '80%',
  padding: '10px 14px',
  borderRadius: '18px',
  fontSize: '14px',
  lineHeight: '1.55',
  wordBreak: 'break-word',
};

const typingDotStyle: React.CSSProperties = {
  width: '7px',
  height: '7px',
  borderRadius: '50%',
  animation: 'demoChatTypingDot 1.2s infinite',
};

const limitWarningStyle: React.CSSProperties = {
  textAlign: 'center',
  fontSize: '12px',
  padding: '8px',
  fontWeight: 500,
};

const inputContainerStyle: React.CSSProperties = {
  padding: '12px 16px',
  paddingBottom: 'max(12px, env(safe-area-inset-bottom, 12px))',
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  flexShrink: 0,
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  padding: '10px 16px',
  borderRadius: '22px',
  fontSize: '16px', /* 16px prevents iOS zoom on focus */
  outline: 'none',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
};

const sendButtonStyle: React.CSSProperties = {
  width: '38px',
  height: '38px',
  borderRadius: '50%',
  border: 'none',
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 0.15s ease, filter 0.15s ease, background 0.2s ease',
  flexShrink: 0,
  WebkitTapHighlightColor: 'transparent',
};

const poweredByStyle: React.CSSProperties = {
  textAlign: 'center',
  fontSize: '11px',
  padding: '8px 16px',
  flexShrink: 0,
  letterSpacing: '0.01em',
};
